/**
 * Report Filters Component
 * Filters for GPS reports (date range, device selection, report type)
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ScrollView,
  TextInput,
  Modal,
  Pressable,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import DateTimePicker from '@react-native-community/datetimepicker';
import gpsTrackingAPI from '../../api/services/gpsTracking';
import type { GPSDevice } from '../../types/gps';

export type ReportType = 'trips' | 'summary' | 'stops' | 'routes' | 'events';

export interface ReportFiltersData {
  reportType: ReportType;
  deviceId?: number;
  fromDate: Date;
  toDate: Date;
}

interface ReportFiltersProps {
  onApply: (filters: ReportFiltersData) => void;
  onDeviceSelect?: (deviceId: number | undefined) => void;
  initialFilters?: Partial<ReportFiltersData>;
}

type DateRangePreset = 'today' | 'yesterday' | 'last7days' | 'last30days' | 'thisMonth' | 'custom';

export default function ReportFilters({ onApply, onDeviceSelect, initialFilters }: ReportFiltersProps) {
  const [reportType, setReportType] = useState<ReportType>('routes');
  const [selectedDevice, setSelectedDevice] = useState<number | undefined>();
  const [devices, setDevices] = useState<GPSDevice[]>([]);
  const [datePreset, setDatePreset] = useState<DateRangePreset>('last7days');
  const [fromDate, setFromDate] = useState<Date>(getDateForPreset('last7days').from);
  const [toDate, setToDate] = useState<Date>(getDateForPreset('last7days').to);
  const [loading, setLoading] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFromDatePicker, setShowFromDatePicker] = useState(false);
  const [showToDatePicker, setShowToDatePicker] = useState(false);

  const formatDateTimeLocal = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const parseDateTimeLocal = (value: string) => {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  useEffect(() => {
    loadDevices();
  }, []);
  
  useEffect(() => {
    if (!initialFilters) return;
    
    if (initialFilters.reportType) {
      setReportType(initialFilters.reportType);
    }
    if (typeof initialFilters.deviceId !== 'undefined') {
      setSelectedDevice(initialFilters.deviceId);
    }
    if (initialFilters.fromDate) {
      setFromDate(initialFilters.fromDate);
      setDatePreset('custom');
    }
    if (initialFilters.toDate) {
      setToDate(initialFilters.toDate);
      setDatePreset('custom');
    }
  }, [initialFilters]);

  const loadDevices = async () => {
    try {
      const data = await gpsTrackingAPI.devices.listDevices();
      setDevices(data);
    } catch (error) {
      console.error('Failed to load devices:', error);
    }
  };

  function getDateForPreset(preset: DateRangePreset): { from: Date; to: Date } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (preset) {
      case 'today':
        return { from: today, to: new Date() };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59);
        return { from: yesterday, to: yesterdayEnd };
      
      case 'last7days':
        const last7days = new Date(today);
        last7days.setDate(last7days.getDate() - 7);
        return { from: last7days, to: new Date() };
      
      case 'last30days':
        const last30days = new Date(today);
        last30days.setDate(last30days.getDate() - 30);
        return { from: last30days, to: new Date() };
      
      case 'thisMonth':
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        return { from: firstDay, to: new Date() };
      
      default:
        return { from: fromDate, to: toDate };
    }
  }

  const handlePresetChange = (preset: DateRangePreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const dates = getDateForPreset(preset);
      setFromDate(dates.from);
      setToDate(dates.to);
    }
  };

  const handleFromDateChange = (event: any, selectedDate?: Date) => {
    setShowFromDatePicker(false);
    if (selectedDate) {
      setFromDate(selectedDate);
      setDatePreset('custom');
    }
  };

  const handleToDateChange = (event: any, selectedDate?: Date) => {
    setShowToDatePicker(false);
    if (selectedDate) {
      setToDate(selectedDate);
      setDatePreset('custom');
    }
  };

  const handleApply = () => {
    onApply({
      reportType,
      deviceId: selectedDevice,
      fromDate,
      toDate,
    });
  };

  const handleDeviceChange = (deviceId: number | undefined) => {
    setSelectedDevice(deviceId);
    setShowDeviceModal(false);
    setSearchQuery('');
    if (onDeviceSelect) {
      onDeviceSelect(deviceId);
    }
  };

  // Filter devices based on search query
  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devices;
    
    const query = searchQuery.toLowerCase();
    return devices.filter(device => {
      return (
        device.label?.toLowerCase().includes(query) ||
        device.imei?.toLowerCase().includes(query) ||
        device.current_vehicle?.registration_number?.toLowerCase().includes(query)
      );
    });
  }, [devices, searchQuery]);

  const selectedDeviceLabel = useMemo(() => {
    if (!selectedDevice) return 'Todos os veículos';
    const device = devices.find(d => d.id === selectedDevice);
    if (!device) return 'Desconhecido';
    return device.current_vehicle?.registration_number || device.label;
  }, [selectedDevice, devices]);

  const reportTypes: { id: ReportType; label: string; icon: string }[] = [
    { id: 'routes', label: 'Rotas', icon: 'navigate' },
    { id: 'events', label: 'Eventos', icon: 'notifications' },
    // Temporarily hidden until fully tested:
    // { id: 'trips', label: 'Viagens', icon: 'car-sport' },
    // { id: 'summary', label: 'Sumário', icon: 'stats-chart' },
    // { id: 'stops', label: 'Paragens', icon: 'pause-circle' },
  ];

  const presets: { id: DateRangePreset; label: string }[] = [
    { id: 'today', label: 'Hoje' },
    { id: 'yesterday', label: 'Ontem' },
    { id: 'last7days', label: 'Últimos 7 dias' },
    { id: 'last30days', label: 'Últimos 30 dias' },
    { id: 'thisMonth', label: 'Este mês' },
    { id: 'custom', label: 'Personalizado' },
  ];

  return (
    <View style={styles.container}>
      {/* Report Type Selector */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Tipo de Relatório</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.typeScroll}>
          <View style={styles.typeRow}>
            {reportTypes.map((type) => (
              <TouchableOpacity
                key={type.id}
                style={[
                  styles.typeButton,
                  reportType === type.id && styles.typeButtonActive,
                ]}
                onPress={() => setReportType(type.id)}
              >
                <Ionicons
                  name={type.icon as any}
                  size={20}
                  color={reportType === type.id ? '#FFFFFF' : '#007AFF'}
                />
                <Text
                  style={[
                    styles.typeButtonText,
                    reportType === type.id && styles.typeButtonTextActive,
                  ]}
                >
                  {type.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Date Range Preset */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Período</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.presetScroll}>
          <View style={styles.presetRow}>
            {presets.map((preset) => (
              <TouchableOpacity
                key={preset.id}
                style={[
                  styles.presetButton,
                  datePreset === preset.id && styles.presetButtonActive,
                ]}
                onPress={() => handlePresetChange(preset.id)}
              >
                <Text
                  style={[
                    styles.presetButtonText,
                    datePreset === preset.id && styles.presetButtonTextActive,
                  ]}
                >
                  {preset.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
        
        {datePreset === 'custom' && (
          <View style={styles.customDateContainer}>
            <View style={styles.datePickerRow}>
              <Text style={styles.datePickerLabel}>De:</Text>
              {Platform.OS === 'web' ? (
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(fromDate)}
                  max={formatDateTimeLocal(toDate)}
                  onChange={(e) => {
                    const newDate = parseDateTimeLocal(e.target.value);
                    if (newDate) setFromDate(newDate);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #E5E5EA',
                    backgroundColor: '#F2F2F7',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#000000',
                    outline: 'none',
                  }}
                />
              ) : (
                <TouchableOpacity
                  style={styles.datePickerButton}
                  onPress={() => setShowFromDatePicker(true)}
                >
                  <Ionicons name="calendar-outline" size={14} color="#007AFF" />
                  <Text style={styles.datePickerText}>
                    {fromDate.toLocaleString('pt-PT')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
            
            <View style={styles.datePickerRow}>
              <Text style={styles.datePickerLabel}>Até:</Text>
              {Platform.OS === 'web' ? (
                <input
                  type="datetime-local"
                  value={formatDateTimeLocal(toDate)}
                  min={formatDateTimeLocal(fromDate)}
                  max={formatDateTimeLocal(new Date())}
                  onChange={(e) => {
                    const newDate = parseDateTimeLocal(e.target.value);
                    if (newDate) setToDate(newDate);
                  }}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px solid #E5E5EA',
                    backgroundColor: '#F2F2F7',
                    fontSize: 12,
                    fontWeight: 500,
                    color: '#000000',
                    outline: 'none',
                  }}
                />
              ) : (
                <TouchableOpacity
                  style={styles.datePickerButton}
                  onPress={() => setShowToDatePicker(true)}
                >
                  <Ionicons name="calendar-outline" size={14} color="#007AFF" />
                  <Text style={styles.datePickerText}>
                    {toDate.toLocaleString('pt-PT')}
                  </Text>
                </TouchableOpacity>
              )}
            </View>
          </View>
        )}
        
        {datePreset !== 'custom' && (
          <View style={styles.dateInfo}>
            <Ionicons name="calendar-outline" size={14} color="#8E8E93" />
            <Text style={styles.dateInfoText}>
              {fromDate.toLocaleString('pt-PT')} - {toDate.toLocaleString('pt-PT')}
            </Text>
          </View>
        )}
      </View>

      {/* Date Pickers - Web only uses native input */}
      {Platform.OS !== 'web' && showFromDatePicker && (
        <DateTimePicker
          value={fromDate}
          mode="datetime"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={handleFromDateChange}
          maximumDate={toDate}
        />
      )}
      
      {Platform.OS !== 'web' && showToDatePicker && (
        <DateTimePicker
          value={toDate}
          mode="datetime"
          display={Platform.OS === 'ios' ? 'spinner' : 'default'}
          onChange={handleToDateChange}
          minimumDate={fromDate}
          maximumDate={new Date()}
        />
      )}

      {/* Device Selector */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Veículo</Text>
        <TouchableOpacity
          style={styles.deviceSelectButton}
          onPress={() => setShowDeviceModal(true)}
        >
          <View style={styles.deviceSelectContent}>
            <Ionicons name="car" size={18} color="#007AFF" />
            <Text style={styles.deviceSelectText}>{selectedDeviceLabel}</Text>
          </View>
          <Ionicons name="chevron-down" size={18} color="#8E8E93" />
        </TouchableOpacity>
      </View>

      {/* Device Selection Modal */}
      <Modal
        visible={showDeviceModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowDeviceModal(false)}
      >
        <Pressable 
          style={styles.modalOverlay}
          onPress={() => setShowDeviceModal(false)}
        >
          <Pressable 
            style={styles.modalContent}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Selecionar Veículo</Text>
              <TouchableOpacity
                onPress={() => setShowDeviceModal(false)}
                hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
              >
                <Ionicons name="close" size={24} color="#8E8E93" />
              </TouchableOpacity>
            </View>

            {/* Search Input */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={16} color="#8E8E93" style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder="Procurar por matrícula ou dispositivo..."
                placeholderTextColor="#8E8E93"
                value={searchQuery}
                onChangeText={setSearchQuery}
                autoCapitalize="none"
                autoCorrect={false}
              />
              {searchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setSearchQuery('')}
                  style={styles.clearButton}
                >
                  <Ionicons name="close-circle" size={16} color="#8E8E93" />
                </TouchableOpacity>
              )}
            </View>

            {/* Device List */}
            <ScrollView style={styles.deviceList}>
              {/* All Devices Option */}
              <TouchableOpacity
                style={[
                  styles.deviceItem,
                  !selectedDevice && styles.deviceItemSelected,
                ]}
                onPress={() => handleDeviceChange(undefined)}
              >
                <View style={styles.deviceItemContent}>
                  <Ionicons name="apps" size={20} color="#007AFF" />
                  <View style={styles.deviceItemText}>
                    <Text style={styles.deviceItemName}>Todos os veículos</Text>
                    <Text style={styles.deviceItemCount}>{devices.length} dispositivos</Text>
                  </View>
                </View>
                {!selectedDevice && (
                  <Ionicons name="checkmark-circle" size={20} color="#007AFF" />
                )}
              </TouchableOpacity>

              {/* Individual Devices */}
              {filteredDevices.length === 0 ? (
                <View style={styles.emptyState}>
                  <Ionicons name="car-outline" size={48} color="#C7C7CC" />
                  <Text style={styles.emptyStateText}>
                    Nenhum veículo encontrado
                  </Text>
                </View>
              ) : (
                filteredDevices.map((device) => (
                  <TouchableOpacity
                    key={device.id}
                    style={[
                      styles.deviceItem,
                      selectedDevice === device.id && styles.deviceItemSelected,
                    ]}
                    onPress={() => handleDeviceChange(device.id)}
                  >
                    <View style={styles.deviceItemContent}>
                      <Ionicons 
                        name="car" 
                        size={20} 
                        color={device.is_active ? '#007AFF' : '#8E8E93'} 
                      />
                      <View style={styles.deviceItemText}>
                        <Text style={styles.deviceItemName}>
                          {device.current_vehicle?.registration_number || device.label}
                        </Text>
                        <Text style={styles.deviceItemSubtext}>
                          {device.current_vehicle?.registration_number ? device.label : `IMEI: ${device.imei}`}
                        </Text>
                      </View>
                    </View>
                    {selectedDevice === device.id && (
                      <Ionicons name="checkmark-circle" size={20} color="#007AFF" />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Apply Button */}
      <TouchableOpacity
        style={styles.applyButton}
        onPress={handleApply}
        disabled={loading}
      >
        <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
        <Text style={styles.applyButtonText}>Gerar Relatório</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  section: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#8E8E93',
    marginBottom: 6,
    textTransform: 'uppercase',
  },
  typeScroll: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  typeRow: {
    flexDirection: 'row',
    gap: 8,
  },
  typeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  typeButtonActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  typeButtonText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#007AFF',
  },
  typeButtonTextActive: {
    color: '#FFFFFF',
  },
  presetScroll: {
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  presetRow: {
    flexDirection: 'row',
    gap: 8,
  },
  presetButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  presetButtonActive: {
    backgroundColor: '#E5F1FF',
    borderColor: '#007AFF',
  },
  presetButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#8E8E93',
  },
  presetButtonTextActive: {
    color: '#007AFF',
    fontWeight: '600',
  },
  dateInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 6,
    paddingTop: 6,
    borderTopWidth: 1,
    borderTopColor: '#F2F2F7',
  },
  dateInfoText: {
    fontSize: 11,
    color: '#8E8E93',
  },
  customDateContainer: {
    marginTop: 8,
    gap: 8,
  },
  datePickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  datePickerLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: '#8E8E93',
    width: 35,
  },
  datePickerButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  datePickerText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#000000',
  },
  deviceSelectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: '#F2F2F7',
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  deviceSelectContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flex: 1,
  },
  deviceSelectText: {
    fontSize: 13,
    fontWeight: '500',
    color: '#000000',
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#000000',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F2F2F7',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    margin: 16,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: '#000000',
    padding: 0,
  },
  clearButton: {
    padding: 4,
  },
  deviceList: {
    maxHeight: 400,
  },
  deviceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  deviceItemSelected: {
    backgroundColor: '#F0F7FF',
  },
  deviceItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  deviceItemText: {
    flex: 1,
  },
  deviceItemName: {
    fontSize: 15,
    fontWeight: '500',
    color: '#000000',
    marginBottom: 2,
  },
  deviceItemSubtext: {
    fontSize: 12,
    color: '#8E8E93',
  },
  deviceItemCount: {
    fontSize: 12,
    color: '#8E8E93',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 12,
  },
  applyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    backgroundColor: '#007AFF',
    paddingVertical: 10,
    borderRadius: 8,
    marginTop: 6,
  },
  applyButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
